/**
 * "My tickets" — everything the signed-in employee has raised with IT, and,
 * on a second tab, the requests someone else raised that name them (2 Oct).
 *
 * The list is server-filtered, server-sorted and server-paged: the contract
 * scopes `listTickets` to the caller and there is no requester parameter, so
 * the grid never holds more than one page and never re-sorts locally. That is
 * why the sortable columns deliberately have no `accessor` — an accessor would
 * make `DataGrid` re-sort the current page on top of the server's ordering and
 * quietly disagree with page 2.
 *
 * 6 Oct (product owner): the access cases the reader is involved in come
 * first on page one, one row each with an "Access" type, opening the
 * person's access page; their Ivanti requests are grouped under the row and
 * left out of the tickets (`lib/access-rows.ts`).
 */

import {
  ChevronRight,
  Icon,
  KeyRound,
  MessageCircleQuestionMark,
  Monitor,
  Ticket,
  Users,
} from "@norican/icons";
import {
  Alert,
  Badge,
  Button,
  DataGrid,
  type DataGridColumn,
  type DataGridSort,
  Pagination,
  Tabs,
  TextLink,
  Typography,
} from "@norican/react";
import { type MouseEvent, useCallback, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import {
  AppBreadcrumbs,
  EmptyStateBlock,
  ErrorState,
  isRequesterUnresolvedResult,
  LoadingRows,
  PageContainer,
  RelativeTime,
  ResultStateNotice,
  SectionCard,
} from "@/components/common";
import { useMyAccessCases } from "@/features/access/api";
import { StarterActionLink } from "@/features/starters";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";
import { useKiosk, useMe } from "@/lib/api";
import { logger } from "@/lib/logger";

import type { TicketSummary } from "./api/contract";
import { useTicketList, useTranslateTo } from "./api/queries";
import {
  AccessActionBadge,
  AccessCaseCardRow,
  AccessKindBadge,
  AccessRequestsExpander,
  AccessStageBadge,
  useAccessRowTitle,
} from "./components/access-case-row";
import { NamedOnList } from "./components/named-on";
import { SlaCell } from "./components/sla-indicator";
import {
  TicketKindBadge,
  TicketPriorityBadge,
  TicketStatusBadge,
} from "./components/ticket-badges";
import { TicketErrorBoundary } from "./components/ticket-error-boundary";
import { TicketFilters } from "./components/ticket-filters";
import { TranslatableTextView } from "./components/translatable-text";
import { TranslationPreferenceProvider } from "./components/translation-preference";
import {
  accessRowsFor,
  groupedTicketIds,
  mergeTicketRows,
  type TicketListRow,
  withoutGrouped,
} from "./lib/access-rows";
import { type TicketListView, ticketListView } from "./lib/named-on";
import {
  currentRowFor,
  LAST_OPENED_KEY,
  opensRow,
  ROW_CONTROLS,
  ROW_LINK_ATTRIBUTE,
} from "./lib/row-open";
import { columnForSortField, sortFieldForColumn, ticketReferenceKey } from "./lib/ticket-display";
import { TICKET_PAGE_SIZE, useTicketListParams } from "./lib/use-ticket-list-params";

export function TicketListPage() {
  const kiosk = useKiosk();
  if (kiosk !== null) {
    return <KioskTicketListNotice />;
  }
  return (
    <TicketErrorBoundary>
      <TranslationPreferenceProvider>
        <TicketListView />
      </TranslationPreferenceProvider>
    </TicketErrorBoundary>
  );
}

/**
 * What `/tickets` says on a shared device instead of a list (1 Oct).
 *
 * Every ticket the device account could list would be everyone's, so the
 * server returns none; saying why beats an empty list that reads as "IT lost
 * my ticket".
 */
function KioskTicketListNotice() {
  const { t } = useT("tickets");
  useDocumentTitle(t("title"));
  return (
    <PageContainer title={t("title")}>
      <EmptyStateBlock
        icon={<Icon icon={Monitor} size="lg" tone="accent" />}
        title={t("kiosk.listHiddenTitle")}
        description={t("kiosk.listHiddenBody")}
        actions={
          <Button render={<Link to="/ask" />} variant="outline" tone="neutral">
            {t("emergency.backToHelpDesk")}
          </Button>
        }
      />
    </PageContainer>
  );
}

function TicketListView() {
  const { t } = useT("tickets");
  const { t: tCommon } = useT();
  useDocumentTitle(t("title"));
  const navigate = useNavigate();
  const translateTo = useTranslateTo();
  const { params, setParams, reset, isFiltered, query } = useTicketListParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = ticketListView(searchParams.get("view"));

  // The tab lives in the URL, so "Back" from a named-on request lands on the
  // Named on tab again. The reader's own filters are kept across the switch.
  const setView = useCallback(
    (next: TicketListView) => {
      logger.debug("[tickets] list view", next);
      setSearchParams((previous) => {
        const search = new URLSearchParams(previous);
        if (next === "named-on") {
          search.set("view", next);
        } else {
          search.delete("view");
        }
        return search;
      });
    },
    [setSearchParams],
  );

  const listQuery = useTicketList({ ...query, translate_to: translateTo });
  // 6 Oct: the access cases the reader is involved in, one row each, their
  // requests grouped under them and left out of the tickets below.
  const { data: me } = useMe();
  const accessOn = me?.access_front_door === true;
  const accessQuery = useMyAccessCases(accessOn);
  const accessCases = accessQuery.data?.items;
  const tickets = useMemo(
    () => withoutGrouped(listQuery.data?.items ?? [], groupedTicketIds(accessCases)),
    [listQuery.data?.items, accessCases],
  );
  const rows = useMemo(
    () => mergeTicketRows(accessRowsFor(accessCases, params), tickets),
    [accessCases, params, tickets],
  );
  const rowTitle = useAccessRowTitle();
  // Wait for the cases too (while they can come), so grouped requests do not
  // flash up in the list and then vanish.
  const pending = listQuery.isPending || (accessOn && accessQuery.isPending);
  // Ivanti has no record of this person: the notice above already says so,
  // and "you have no tickets" underneath it would contradict it.
  const requesterUnresolved = isRequesterUnresolvedResult(listQuery.data?.result);
  const page = listQuery.data?.page;

  const sort = useMemo<DataGridSort>(
    () => ({
      columnId: columnForSortField(params.sort),
      direction: params.order === "ASC" ? "asc" : "desc",
    }),
    [params.sort, params.order],
  );

  const handleSortChange = useCallback(
    (next: DataGridSort | null) => {
      // The grid cycles asc → desc → none. "None" is not a state the API has,
      // so it lands back on the default ordering rather than an arbitrary one.
      if (next === null) {
        setParams({ sort: "UPDATED_AT", order: "DESC" });
        return;
      }
      const field = sortFieldForColumn(next.columnId);
      if (field === null) {
        return;
      }
      setParams({ sort: field, order: next.direction === "asc" ? "ASC" : "DESC" });
    },
    [setParams],
  );

  const columns = useMemo<DataGridColumn<TicketListRow<TicketSummary>>[]>(
    () => [
      {
        id: "ticket_number",
        header: t("field.number"),
        sortable: true,
        width: 165,
        cell: (entry) =>
          entry.kind === "access" ? (
            <TextLink
              render={<Link to={entry.access.url_path} />}
              className="font-mono text-sm font-semibold tracking-tight"
            >
              {t("access.caseNumber", { number: entry.access.case_number })}
            </TextLink>
          ) : (
            <TicketNumberCell row={entry.ticket} />
          ),
      },
      {
        id: "subject",
        header: t("field.subject"),
        cell: (entry) =>
          entry.kind === "access" ? (
            <span className="flex flex-col items-start gap-1">
              <TextLink render={<Link to={entry.access.url_path} />} className="font-medium">
                {rowTitle(entry.access)}
              </TextLink>
              <AccessActionBadge item={entry.access} />
              <AccessRequestsExpander requests={entry.access.requests ?? []} />
            </span>
          ) : (
            <TicketSubjectCell row={entry.ticket} />
          ),
      },
      {
        id: "kind",
        header: t("field.type"),
        width: 150,
        cell: (entry) =>
          entry.kind === "access" ? (
            <AccessKindBadge size="sm" />
          ) : (
            <TicketKindBadge kind={entry.ticket.kind} size="sm" />
          ),
      },
      {
        id: "status",
        header: t("field.status"),
        sortable: true,
        width: 140,
        cell: (entry) =>
          entry.kind === "access" ? (
            <AccessStageBadge item={entry.access} />
          ) : (
            <TicketStatusBadge status={entry.ticket.status} kind={entry.ticket.kind} />
          ),
      },
      {
        id: "priority",
        header: t("field.priority"),
        sortable: true,
        width: 110,
        cell: (entry) =>
          entry.kind === "access" ? null : (
            <TicketPriorityBadge code={entry.ticket.priority_code} />
          ),
      },
      {
        id: "target_at",
        header: t("field.target"),
        width: 130,
        cell: (entry) => (entry.kind === "access" ? null : <SlaCell ticket={entry.ticket} />),
      },
      {
        id: "updated_at",
        header: t("field.updated"),
        sortable: true,
        width: 140,
        cell: (entry) => {
          const updated =
            entry.kind === "access" ? entry.access.updated_at : entry.ticket.updated_at;
          return updated ? <RelativeTime value={updated} /> : null;
        },
      },
      {
        // 14 Oct: an explicit way in on every row, and the link a click
        // anywhere on the row follows (keyboard: Tab to it, Enter).
        id: "open",
        header: <span className="sr-only">{t("list.open")}</span>,
        width: 96,
        cell: (entry) => {
          const to = entry.kind === "access" ? entry.access.url_path : entry.ticket.url_path;
          const number =
            entry.kind === "access"
              ? t("access.caseNumber", { number: entry.access.case_number })
              : t(ticketReferenceKey(entry.ticket.kind, "id"), {
                  number: entry.ticket.ticket_number,
                });
          return (
            <TextLink
              render={<Link to={to} />}
              {...{ [ROW_LINK_ATTRIBUTE]: entry.id }}
              aria-label={t("list.openTicket", { number })}
              className="inline-flex items-center gap-0.5 text-sm font-medium whitespace-nowrap"
            >
              {t("list.open")}
              <Icon icon={ChevronRight} size="sm" />
            </TextLink>
          );
        },
      },
    ],
    [t, rowTitle],
  );

  /** A click on a row's background follows the row's Open link (14 Oct). */
  const openRow = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const row = target.closest("tbody tr");
    const link = row?.querySelector<HTMLAnchorElement>(`a[${ROW_LINK_ATTRIBUTE}]`);
    if (!row || !link) {
      return;
    }
    rememberOpened(link.getAttribute(ROW_LINK_ATTRIBUTE));
    const go = opensRow({
      button: event.button,
      metaKey: event.metaKey,
      ctrlKey: event.ctrlKey,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
      onControl: target.closest(ROW_CONTROLS) !== null,
      selecting: (window.getSelection()?.toString() ?? "") !== "",
    });
    const href = link.getAttribute("href");
    if (go && href) {
      void navigate(href);
    }
  };

  const pageCount =
    page?.total != null
      ? Math.max(1, Math.ceil(page.total / TICKET_PAGE_SIZE))
      : params.page + (page?.has_more === true ? 1 : 0);

  // No "New ticket" control, here or in the empty state. A ticket is the
  // outcome of a conversation that could not be resolved any other way, not a
  // form to be filled in first — so the only way to open one is to describe the
  // problem and let the assistant decide it needs raising. What an employee
  // with an empty list needs is not a blank form; it is the way back to Ask.
  const askButton = (
    <Button
      startIcon={<Icon icon={MessageCircleQuestionMark} size="sm" />}
      onClick={() => void navigate("/ask")}
    >
      {t("list.empty.action")}
    </Button>
  );

  return (
    <PageContainer
      // The brand's condensed display face (Turbine) on the page title. It is
      // the one place on an otherwise dense table page where the product gets
      // to sound like itself, and it costs the reader nothing.
      title={
        <Typography as="span" variant="display-md" className="text-3xl sm:text-4xl">
          {t("title")}
        </Typography>
      }
      // The header carries no navigation any more, so this is the way back to
      // the conversation. The logo is also a link home, but a breadcrumb says
      // so out loud rather than relying on the convention being noticed.
      breadcrumbs={<AppBreadcrumbs items={[{ label: t("title") }]} />}
      subtitle={t("subtitle")}
      width="wide"
      // 28 Oct (Rusty, v6): anyone may start Change access; this is its one way in.
      actions={
        me?.access_front_door === true ? (
          <Button
            variant="outline"
            tone="neutral"
            size="sm"
            startIcon={<Icon icon={KeyRound} size="sm" />}
            render={<Link to="/access/new" />}
          >
            {t("access.glossary.changeAccess")}
          </Button>
        ) : null
      }
    >
      {/* Only when a new starter case waits on the reader (2 Oct). */}
      <StarterActionLink className="mb-4" />
      {/* Always visible, even when nothing names the reader: the empty state
          is what explains the feature exists (2 Oct). */}
      <Tabs.Root value={view} onValueChange={(value) => setView(value as TicketListView)}>
        <Tabs.List aria-label={t("tabs.label")}>
          <Tabs.Tab value="mine">
            <span className="flex items-center gap-2">
              <Icon icon={Ticket} size="sm" />
              {t("tabs.mine")}
            </span>
          </Tabs.Tab>
          <Tabs.Tab value="named-on">
            <span className="flex items-center gap-2">
              <Icon icon={Users} size="sm" />
              {t("tabs.namedOn")}
            </span>
          </Tabs.Tab>
          <Tabs.Indicator />
        </Tabs.List>

        <Tabs.Panel value="named-on">
          <SectionCard padding="md" className="mt-6">
            <NamedOnList />
          </SectionCard>
        </Tabs.Panel>

        <Tabs.Panel value="mine">
          <div className="mt-6 flex flex-col gap-6">
            {/* The filter bar sits outside the results card, not inside it. It is a
          control surface, not content: giving it its own row lets the toolbar
          chrome read as a bar rather than as a nested card, and keeps the
          results card a single uninterrupted block of data. */}
            <TicketFilters
              params={params}
              onChange={setParams}
              onReset={reset}
              isFiltered={isFiltered}
            />

            <SectionCard padding="md">
              <div className="flex flex-col gap-4">
                {/* One wording for "we could not ask" across every list in the
              product, so an outage cannot be mistaken for an empty inbox on
              one page while being explained on another. */}
                <ResultStateNotice result={listQuery.data?.result} />

                {listQuery.data?.partial_translation === true ? (
                  <Alert tone="info">{t("translated.partial")}</Alert>
                ) : null}

                {pending ? (
                  <LoadingRows rows={6} />
                ) : listQuery.isError ? (
                  <ErrorState error={listQuery.error} onRetry={() => void listQuery.refetch()} />
                ) : rows.length === 0 ? (
                  requesterUnresolved ? null : (
                    <EmptyStateBlock
                      icon={<Icon icon={Ticket} size="lg" tone="accent" />}
                      title={isFiltered ? t("list.noMatches.title") : t("list.empty.title")}
                      description={
                        isFiltered ? t("list.noMatches.description") : t("list.empty.description")
                      }
                      actions={
                        isFiltered ? (
                          <Button variant="outline" tone="neutral" onClick={reset}>
                            {t("filter.clear")}
                          </Button>
                        ) : (
                          // Unfiltered means the default OPEN bucket, so "empty" only
                          // says nothing is open: resolved and closed tickets may well
                          // exist. Checking would cost another Ivanti round trip, so
                          // the way to them is always offered instead.
                          <>
                            <Button
                              variant="outline"
                              tone="neutral"
                              onClick={() => setParams({ bucket: "ALL" })}
                            >
                              {t("list.empty.showAll")}
                            </Button>
                            {askButton}
                          </>
                        )
                      }
                    />
                  )
                ) : (
                  <>
                    {/* `fixed`, not the default `auto`. On auto the table sizes to
                  content, so one long subject stretched that column to ~800px
                  and pushed Status, Priority, Target and Last update off the
                  right edge behind a horizontal scroll. Fixed makes every
                  column honour its declared width and gives Subject whatever
                  is left, so the grid always fits its container and long
                  subjects wrap instead of shoving the table sideways. */}
                    {/* Phones and narrow tablets get stacked cards instead of the
                  grid: seven fixed-width columns cannot share 390px, and the
                  grid then clipped Subject after a dozen characters and hid
                  Type behind a sideways scroll. */}
                    <Typography variant="caption" tone="muted">
                      {t("list.rowHint")}
                    </Typography>
                    <TicketCardList rows={rows} />

                    {/* 14 Oct: the whole row opens its ticket (pointer, hover
                        tint); the row last opened is the grid's current row. */}
                    <div
                      className="[&_tbody_tr:hover]:bg-surface-hover hidden md:block [&_tbody_tr]:cursor-pointer"
                      onClick={openRow}
                      role="presentation"
                    >
                      <DataGrid
                        tableLayout="fixed"
                        columns={columns}
                        data={rows}
                        currentRowId={currentRowFor(
                          readLastOpened(),
                          rows.map((row) => row.id),
                        )}
                        getRowId={(row) => row.id}
                        sort={sort}
                        onSortChange={handleSortChange}
                        aria-label={t("title")}
                      />
                    </div>

                    <div className="border-border-subtle flex flex-wrap items-center justify-between gap-3 border-t pt-4">
                      <Typography variant="label" tone="muted" aria-live="polite">
                        {/* One page: what is on screen, access rows in, grouped
                            requests out. More pages: the server's count. */}
                        {page?.total != null && pageCount > 1
                          ? t("list.count", { count: page.total })
                          : page?.total != null
                            ? t("list.count", { count: rows.length })
                            : t("list.countUnknown", { count: rows.length })}
                      </Typography>

                      <Pagination
                        page={params.page}
                        pageCount={pageCount}
                        onPageChange={(next) => setParams({ page: next })}
                        label={tCommon("pagination.label")}
                        prevLabel={tCommon("pagination.previous")}
                        nextLabel={tCommon("pagination.next")}
                        hideSummary
                        formatStatus={({ page: current, pageCount: count }) =>
                          tCommon("pagination.status", { page: current, pageCount: count })
                        }
                      />
                    </div>
                  </>
                )}
              </div>
            </SectionCard>
          </div>
        </Tabs.Panel>
      </Tabs.Root>
    </PageContainer>
  );
}

/** A ticket's number cell: its reference, and how many updates are new. */
function TicketNumberCell({ row }: { row: TicketSummary }) {
  const { t } = useT("tickets");
  return (
    <span className="flex items-center gap-2">
      <TextLink
        render={<Link to={row.url_path} />}
        className="font-mono text-sm font-semibold tracking-tight"
      >
        {t(ticketReferenceKey(row.kind, "id"), { number: row.ticket_number })}
      </TextLink>
      {(row.unread_updates ?? 0) > 0 ? (
        <Badge tone="accent" variant="solid" size="xs">
          {t("list.unread", { count: row.unread_updates ?? 0 })}
        </Badge>
      ) : null}
    </span>
  );
}

/** A ticket's subject cell, with "Waiting for you" under it when it is. */
function TicketSubjectCell({ row }: { row: TicketSummary }) {
  const { t } = useT("tickets");
  return (
    <span className="flex flex-col items-start gap-1">
      <TextLink render={<Link to={row.url_path} />} className="font-medium">
        <TranslatableTextView value={row.subject} hideBadge />
      </TextLink>
      {row.awaiting_user === true ? (
        <Badge tone="warning" variant="solid" size="xs">
          {t("list.awaitingYou")}
        </Badge>
      ) : null}
    </span>
  );
}

interface TicketCardListProps {
  rows: TicketListRow<TicketSummary>[];
}

/**
 * The ticket list below `md`: one card per ticket, Subject across the full
 * width and the reference shortened to "#10421" (the Type badge beside it
 * already says incident or request). An access case is its own card (6 Oct).
 */
function TicketCardList({ rows }: TicketCardListProps) {
  const { t } = useT("tickets");

  return (
    <ul className="flex flex-col gap-2 md:hidden" aria-label={t("title")}>
      {rows.map((entry) => {
        if (entry.kind === "access") {
          return (
            <li key={entry.id}>
              <AccessCaseCardRow item={entry.access} openLabel />
            </li>
          );
        }
        const row = entry.ticket;
        return (
          <li key={entry.id}>
            <Link
              to={row.url_path}
              onClick={() => rememberOpened(entry.id)}
              aria-label={t("list.openTicket", {
                number: t(ticketReferenceKey(row.kind, "id"), { number: row.ticket_number }),
              })}
              className="border-border-subtle hover:bg-surface-hover hover:border-accent-border focus-visible:ring-accent flex min-w-0 cursor-pointer flex-col gap-2 rounded-md border p-3 transition-colors focus-visible:ring-2 focus-visible:outline-none"
            >
              <span className="flex min-w-0 flex-wrap items-center gap-2">
                <span className="text-accent-fg-subtle font-mono text-sm font-semibold tracking-tight">
                  <span aria-hidden>#{row.ticket_number}</span>
                  <span className="sr-only">
                    {t(ticketReferenceKey(row.kind, "id"), { number: row.ticket_number })}
                  </span>
                </span>
                <TicketKindBadge kind={row.kind} size="sm" />
                {(row.unread_updates ?? 0) > 0 ? (
                  <Badge tone="accent" variant="solid" size="xs">
                    {t("list.unread", { count: row.unread_updates ?? 0 })}
                  </Badge>
                ) : null}
              </span>
              <span className="text-accent-fg-subtle font-medium break-words underline underline-offset-2">
                <TranslatableTextView value={row.subject} hideBadge />
              </span>
              <span className="flex min-w-0 flex-wrap items-center gap-2">
                <TicketStatusBadge status={row.status} kind={row.kind} />
                {row.awaiting_user === true ? (
                  <Badge tone="warning" variant="solid" size="xs">
                    {t("list.awaitingYou")}
                  </Badge>
                ) : null}
                <Typography as="span" variant="caption" tone="muted" className="ml-auto">
                  <RelativeTime value={row.updated_at} />
                </Typography>
                {/* 14 Oct: a visible way in at the end of each card. */}
                <span
                  aria-hidden
                  className="text-accent-fg-subtle inline-flex items-center gap-0.5 text-sm font-medium"
                >
                  {t("list.open")}
                  <Icon icon={ChevronRight} size="sm" />
                </span>
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/** The row opened last this session, for the grid's current row (14 Oct). */
function readLastOpened(): string | null {
  try {
    return window.sessionStorage.getItem(LAST_OPENED_KEY);
  } catch {
    return null;
  }
}

function rememberOpened(id: string | null) {
  try {
    if (id) {
      window.sessionStorage.setItem(LAST_OPENED_KEY, id);
    }
  } catch {
    // Storage off (private mode): the current row is a nicety.
  }
}
